import { SegmentedMessage } from "sms-segments-calculator";

import { normalizeAuthoringDraft } from "./authoringState";
import type { TextComposerAuthoringState } from "./authoringState";
import type { TextComposerExtension } from "./extensionContract";
import {
  composeValidation,
  type ComposedValidationResult,
} from "./extensionRegistry";
import type { MessageDraftState } from "./messageDraftStore";
import {
  getDerivedOutput,
  getLinkedDerivedOutput,
  getTransformForChannel,
  type TextComposerChannelTransformValue,
} from "../channel/channelDerivation";
import { buildChannelTransforms } from "../channel/channelTransforms";
import { MMS_BODY_CHARACTER_LIMIT } from "../channel/mmsConstraints";
import { defaultExtensions } from "../extensions/defaultExtensions";
import { resolveProductGridModelForDragonTileCategory } from "../extensions/mergeTags/mergeTagDefinitions";
import {
  hasProductContextMarkers,
  stripProductScaffoldLines,
} from "../extensions/mergeTags/productContexts";
import {
  buildFrameOutput,
  mapChannelToPreviewMedium,
} from "../frame/frameOutput";
import { stripUrls } from "../preview/previewContentUtils";

import { OFFER_PLACEHOLDER_RE } from "@/grapesPlugins/email/OfferBlock/offerUtils";
import { MERGE_TAG_PATTERN } from "@/services/mergeTags";
import { hasProductContextTags } from "@/services/mergeTags";

const WARNING_CHARACTER_BUFFER = 20;
const REMAINING_MERGE_TAG_PATTERN = /\{\{[^}]+\}\}/;
const OFFER_TAG_IN_BODY_PATTERN = /\{%\s*offer\s+'[^']+'\s*%\}/;

function extractBodyTextFromChannelTransform(
  transform: TextComposerChannelTransformValue,
): string {
  if (!transform) return "";
  const body = transform.content.body;
  if (body.type === "TEXT") return body.text;
  if (body.type === "IMAGE" && "text" in body && body.text) return body.text;
  return "";
}

function selectTransformedBodyTextForActiveChannel(
  state: MessageDraftState,
): string {
  const transforms = selectChannelTransforms(state);
  const channel = selectActivePreviewChannel(state);
  const transform = getTransformForChannel(transforms, channel);
  return extractBodyTextFromChannelTransform(transform);
}

function hasMergeTagsInAuthoringBody(bodyText: string): boolean {
  MERGE_TAG_PATTERN.lastIndex = 0;
  return MERGE_TAG_PATTERN.test(bodyText);
}

function hasMergeTagsInTransformedBody(transformedBody: string): boolean {
  if (!transformedBody) return false;
  if (hasProductContextTags(transformedBody)) return true;
  return REMAINING_MERGE_TAG_PATTERN.test(transformedBody);
}

function hasOfferPlaceholderInText(
  authoringBody: string,
  transformedBody: string,
): boolean {
  OFFER_PLACEHOLDER_RE.lastIndex = 0;
  if (OFFER_PLACEHOLDER_RE.test(authoringBody)) return true;
  return (
    OFFER_TAG_IN_BODY_PATTERN.test(authoringBody) ||
    OFFER_TAG_IN_BODY_PATTERN.test(transformedBody)
  );
}

function hasOfferInMessage(
  authoringBody: string,
  transformedBody: string,
  offerSlug: string | undefined,
): boolean {
  if (offerSlug?.trim()) return true;
  return hasOfferPlaceholderInText(authoringBody, transformedBody);
}

export function selectIsCharacterCountApproximate(
  state: MessageDraftState,
): boolean {
  const normalized = normalizeAuthoringDraft(state.authoringState);
  const authoringBody = normalized.bodyText ?? "";
  const transformedBody = selectTransformedBodyTextForActiveChannel(state);

  if (hasMergeTagsInAuthoringBody(authoringBody)) return true;
  // A product context can hold only plain copy (no {{ }} tags) and still drop
  // whole sections at render time, so its markers alone make counts estimates.
  if (hasProductContextMarkers(authoringBody)) return true;
  if (hasMergeTagsInTransformedBody(transformedBody)) return true;
  if (
    hasOfferInMessage(authoringBody, transformedBody, normalized.offer?.slug)
  ) {
    return true;
  }

  return false;
}

/** RCS Rich body limit. Only applied when the RCS Rich flag is on. */
export const RCS_BODY_CHARACTER_LIMIT = 3072;

const GSM_7_SINGLE_SEGMENT_LIMIT = 160;
const GSM_7_MULTI_SEGMENT_LIMIT = 153;
const UCS_2_SINGLE_SEGMENT_LIMIT = 70;
const UCS_2_MULTI_SEGMENT_LIMIT = 67;

export function selectNormalizedAuthoringState(state: MessageDraftState) {
  return normalizeAuthoringDraft(selectNormalizedAuthoringState_inner(state));
}

function selectNormalizedAuthoringState_inner(state: MessageDraftState) {
  return state.authoringState;
}

export function selectDerivedOutput(state: MessageDraftState) {
  return getDerivedOutput(normalizeAuthoringDraft(state.authoringState));
}

export function selectSelectedProductModel(state: MessageDraftState) {
  return resolveProductGridModelForDragonTileCategory(state.dragonTileCategory);
}

export function selectChannelTransforms(state: MessageDraftState) {
  return buildChannelTransforms(
    normalizeAuthoringDraft(state.authoringState),
    selectSelectedProductModel(state),
  );
}

export function selectFrameOutput(
  state: MessageDraftState,
  fallbackState?: TextComposerAuthoringState,
) {
  const normalized = normalizeAuthoringDraft(state.authoringState);
  const normalizedFallback =
    fallbackState === undefined
      ? undefined
      : normalizeAuthoringDraft(fallbackState);
  const derived = getLinkedDerivedOutput(normalized, normalizedFallback);
  const productModel = selectSelectedProductModel(state);
  const transforms = buildChannelTransforms(normalized, productModel);
  const primaryTransform = getTransformForChannel(
    transforms,
    derived.primaryChannel,
  );
  const fallbackTransform = getTransformForChannel(
    normalizedFallback === undefined
      ? transforms
      : buildChannelTransforms(normalizedFallback, productModel),
    derived.fallbackChannel,
  );
  return buildFrameOutput({
    authoringState: normalized,
    fallbackAuthoringState: normalizedFallback,
    derivedOutput: derived,
    primaryTransform,
    fallbackTransform,
  });
}

export function selectActivePreviewChannel(state: MessageDraftState) {
  const derived = selectDerivedOutput(state);
  return state.previewRecipientViewMode === "fallback" &&
    derived.fallbackChannel !== null
    ? derived.fallbackChannel
    : derived.primaryChannel;
}

export function selectActivePreviewMedium(state: MessageDraftState) {
  return mapChannelToPreviewMedium(selectActivePreviewChannel(state));
}

export interface SmsMetrics {
  smsSegmentCount: number;
  characterCount: number;
  smsCharacterLimit: number;
  encodingName: string;
  singleSegmentLimit: number;
  multiSegmentLimit: number;
}

export function selectSmsMetrics(
  state: MessageDraftState,
  previewBodyText?: string,
): SmsMetrics {
  // Scaffold lines ({% with %}, {% if items.N %}) never reach the recipient,
  // so they don't count. Server previews are already rendered — no markers.
  const bodyText =
    previewBodyText ??
    stripProductScaffoldLines(
      normalizeAuthoringDraft(state.authoringState).bodyText ?? "",
    );
  const segmentedMessage = new SegmentedMessage(bodyText);
  const smsSegmentCount = Math.max(1, segmentedMessage.segmentsCount);
  const isGsmEncoding = segmentedMessage.encodingName === "GSM-7";
  const singleSegmentLimit = isGsmEncoding
    ? GSM_7_SINGLE_SEGMENT_LIMIT
    : UCS_2_SINGLE_SEGMENT_LIMIT;
  const multiSegmentLimit = isGsmEncoding
    ? GSM_7_MULTI_SEGMENT_LIMIT
    : UCS_2_MULTI_SEGMENT_LIMIT;
  const smsCharacterLimit =
    smsSegmentCount === 1
      ? singleSegmentLimit
      : multiSegmentLimit * smsSegmentCount;
  const characterCount = isGsmEncoding
    ? segmentedMessage.numberOfCharacters
    : bodyText.length;
  return {
    smsSegmentCount,
    characterCount,
    smsCharacterLimit,
    encodingName: segmentedMessage.encodingName,
    singleSegmentLimit,
    multiSegmentLimit,
  };
}

export interface DisplayValues {
  transportCountLabel: string;
  messageStatsTooltip: string;
  displayedCharacterCount: number;
  displayedCharacterLimit: number;
  isNearCharacterLimit: boolean;
  showCharacterCountApproximation: boolean;
}

export function selectDisplayValues(
  state: MessageDraftState,
  previewBodyText?: string,
  rcsRichEnabled = false,
): DisplayValues {
  const normalized = normalizeAuthoringDraft(state.authoringState);
  const smsMetrics = selectSmsMetrics(state, previewBodyText);

  const activeChannel = selectActivePreviewChannel(state);
  const isRcsTarget = activeChannel === "RCS";
  const isMmsTarget = activeChannel === "MMS";
  // When RCS is the target, name its actual fallback channel (SMS or MMS)
  // rather than the generic word "fallback".
  const fallbackChannel = selectDerivedOutput(state).fallbackChannel ?? "SMS";
  // RCS carries its own body limit; without the flag it falls back to the
  // SMS-derived one, as it did before RCS Rich.
  const useRcsRichLimit = isRcsTarget && rcsRichEnabled;

  const displayedCharacterLimit = isMmsTarget
    ? MMS_BODY_CHARACTER_LIMIT
    : useRcsRichLimit
      ? RCS_BODY_CHARACTER_LIMIT
      : smsMetrics.smsCharacterLimit;
  const displayedBody =
    previewBodyText ?? stripProductScaffoldLines(normalized.bodyText ?? "");
  const displayedCharacterCount = useRcsRichLimit
    ? // RCS does not charge URL characters against the body limit.
      stripUrls(displayedBody).length
    : isRcsTarget || isMmsTarget
      ? displayedBody.length
      : smsMetrics.characterCount;
  const isNearCharacterLimit =
    displayedCharacterCount >=
    Math.max(0, displayedCharacterLimit - WARNING_CHARACTER_BUFFER);

  // RCS is not segmented, so the RCS Rich label drops the SMS segment count.
  const transportCountLabel = useRcsRichLimit
    ? "RCS"
    : isRcsTarget
      ? `${smsMetrics.smsSegmentCount} RCS`
      : isMmsTarget
        ? "1 MMS"
        : `${smsMetrics.smsSegmentCount} ${smsMetrics.smsSegmentCount === 1 ? "Text" : "Texts"}`;

  // Once a server preview is available the count reflects the rendered body,
  // which the backend prepends with the brand name — surface that at the top of
  // the tooltip so the higher-than-typed count is explained.
  const brandPrependNote =
    previewBodyText !== undefined
      ? "Your brand name is added to the start of each message and is included in this count.\n\n"
      : "";

  const messageStatsTooltip = isMmsTarget
    ? `${brandPrependNote}Transport: MMS\nSegments: 1\nCharacter limit: ${MMS_BODY_CHARACTER_LIMIT}`
    : useRcsRichLimit
      ? `${brandPrependNote}Transport: RCS\nCharacter limit: ${RCS_BODY_CHARACTER_LIMIT} chars\n\nURLs do not count toward the RCS limit.\nRCS is not segmented.\nFallback: ${fallbackChannel} — ${smsMetrics.smsSegmentCount} ${smsMetrics.smsSegmentCount === 1 ? "segment" : "segments"} (${smsMetrics.encodingName}).`
      : `${brandPrependNote}Transport: ${isRcsTarget ? `RCS\nFallback: ${fallbackChannel}` : "Text"}\nEncoding: ${smsMetrics.encodingName}\nSegments: ${smsMetrics.smsSegmentCount}\nCharacter limit: ${smsMetrics.smsCharacterLimit} chars (${smsMetrics.multiSegmentLimit} x ${smsMetrics.smsSegmentCount} segments)\n\nUnicode chars (emoji, smart quotes, arrows) can switch encoding to UCS-2 and reduce segment size.`;

  return {
    transportCountLabel,
    messageStatsTooltip,
    displayedCharacterCount,
    displayedCharacterLimit,
    isNearCharacterLimit,
    showCharacterCountApproximation: selectIsCharacterCountApproximate(state),
  };
}

export function selectValidation(
  state: MessageDraftState,
  extensions?: TextComposerExtension[],
): ComposedValidationResult {
  const normalized = normalizeAuthoringDraft(state.authoringState);
  return composeValidation(extensions ?? defaultExtensions)(normalized);
}

export function selectIsCtaValid(state: MessageDraftState) {
  const validation = selectValidation(state);
  const ctaResult = validation.extensions.cta;
  return ctaResult ? ctaResult.valid : true;
}

export function selectIsSuggestionsValid(state: MessageDraftState) {
  const validation = selectValidation(state);
  const result = validation.extensions.suggestedActions;
  return result ? result.valid : true;
}

// The form is valid iff every extension validates. New extensions are covered
// automatically — no per-extension gate to add at each call site.
export function selectIsAuthoringValid(state: MessageDraftState) {
  return selectValidation(state).valid;
}
